import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Clock, Headset, Mail, MapPin, MessageCircle, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { AIRPORT_OPTIONS, SERVICE_OPTIONS, useLang } from "@/i18n";
import { CONTACT } from "@/data";
import { API_BASE as API } from "@/config";

const EASE = [0.16, 1, 0.3, 1];

const inputCls =
  "w-full rounded-xl border border-blue-500/20 bg-[#0B1526] px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200";

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="mb-1.5 block text-xs font-semibold text-slate-300">{label}</span>
      {children}
    </label>
  );
}

export function QuoteForm() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    service_type: SERVICE_OPTIONS.ar[0],
    aircraft_type: "",
    callsign_reg: "",
    airport: AIRPORT_OPTIONS[0],
    eta_date: "",
    operator_name: "",
    contact_person: "",
    contact_email: "",
    contact_phone: "",
    additional_details: "",
  });
  const [sending, setSending] = useState(false);
  const [sentRef, setSentRef] = useState(null);

  useEffect(() => {
    setForm((f) => ({ ...f, service_type: SERVICE_OPTIONS[lang][0] }));
  }, [lang]);

  useEffect(() => {
    const onBook = (e) => {
      setForm((f) => ({ ...f, airport: e.detail }));
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    };
    window.addEventListener("was:book-airport", onBook);
    return () => window.removeEventListener("was:book-airport", onBook);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/quotes`, { ...form, language: lang });
      setSentRef(data.ref);
      toast.success(`${t("f_success")}: ${data.ref}`, { description: t("f_success_desc") });
    } catch (err) {
      toast.error(t("f_error"));
    } finally {
      setSending(false);
    }
  };

  const waText = encodeURIComponent(
    lang === "ar"
      ? `مرحباً، أود الاستفسار عن خدمة ${form.service_type} في ${form.airport}.`
      : `Hello, I'd like to inquire about ${form.service_type} at ${form.airport}.`
  );

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-32 bg-[#081020] border-t border-blue-500/10 overflow-hidden">
      <div className="absolute inset-0 grid-lines opacity-30" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="lg:col-span-2"
          >
            <span className="font-mono-tag text-xs tracking-[0.3em] uppercase text-blue-400">{t("quote_eyebrow")}</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight text-white" data-testid="quote-title">{t("quote_title")}</h2>
            <p className="mt-4 text-base text-slate-400">{t("quote_sub")}</p>

            <div className="mt-8 rounded-2xl border border-blue-500/20 bg-[#0B1526] p-6" data-testid="contact-info-card">
              <div className="flex items-center gap-2.5 text-white font-bold">
                <Headset size={18} className="text-blue-400" />
                {t("contact_title")}
              </div>
              <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 text-xs font-semibold text-emerald-400">
                <Clock size={12} />
                {t("contact_247")}
              </div>
              <div className="mt-5 space-y-4 text-sm">
                <a data-testid="contact-ops-email" href={`mailto:${CONTACT.opsEmail}`} className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors">
                  <Mail size={16} className="text-blue-400 shrink-0" />
                  <span>{t("contact_ops")}: <span dir="ltr" className="font-mono-tag text-blue-300">{CONTACT.opsEmail}</span></span>
                </a>
                <a data-testid="contact-ceo-email" href={`mailto:${CONTACT.ceoEmail}`} className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors">
                  <Mail size={16} className="text-blue-400 shrink-0" />
                  <span>{t("contact_ceo")}: <span dir="ltr" className="font-mono-tag text-blue-300">{CONTACT.ceoEmail}</span></span>
                </a>
                <a data-testid="contact-phone" href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors">
                  <Phone size={16} className="text-blue-400 shrink-0" />
                  <span dir="ltr" className="font-mono-tag text-blue-300">{CONTACT.phoneDisplay}</span>
                </a>
                <div className="flex items-center gap-3 text-slate-300">
                  <MapPin size={16} className="text-blue-400 shrink-0" />
                  {t("contact_hq_value")}
                </div>
              </div>
              <a
                data-testid="contact-whatsapp-button"
                href={`${CONTACT.whatsapp}?text=${waText}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-6 py-3.5 text-sm font-bold text-white hover:bg-emerald-500 transition-colors duration-200 shadow-[0_0_24px_rgba(16,185,129,0.35)]"
              >
                <MessageCircle size={17} />
                {t("nav_whatsapp")} — <span dir="ltr" className="font-mono-tag">{CONTACT.phoneDisplay}</span>
              </a>
            </div>
          </motion.div>

          <motion.form
            data-testid="quote-request-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="lg:col-span-3 rounded-3xl border border-blue-500/20 bg-[#0B1526] p-6 sm:p-9 shadow-[0_30px_80px_rgba(2,6,17,0.5)]"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("f_service")} testid="field-service-type">
                <select data-testid="quote-service-select" value={form.service_type} onChange={set("service_type")} className={inputCls} required>
                  {SERVICE_OPTIONS[lang].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
              <Field label={t("f_airport")} testid="field-airport">
                <select data-testid="quote-airport-select" value={form.airport} onChange={set("airport")} className={inputCls} required>
                  {AIRPORT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
              <Field label={t("f_aircraft")} testid="field-aircraft">
                <input data-testid="quote-aircraft-input" value={form.aircraft_type} onChange={set("aircraft_type")} className={inputCls} placeholder="A320 · B737 · G650" dir="ltr" />
              </Field>
              <Field label={t("f_callsign")} testid="field-callsign">
                <input data-testid="quote-callsign-input" value={form.callsign_reg} onChange={set("callsign_reg")} className={inputCls} placeholder="5A-XXX / WAS001" dir="ltr" />
              </Field>
              <Field label={t("f_eta")} testid="field-eta">
                <input data-testid="quote-eta-input" type="date" value={form.eta_date} onChange={set("eta_date")} className={inputCls} />
              </Field>
              <Field label={t("f_operator")} testid="field-operator">
                <input data-testid="quote-operator-input" value={form.operator_name} onChange={set("operator_name")} className={inputCls} required />
              </Field>
              <Field label={t("f_person")} testid="field-person">
                <input data-testid="quote-person-input" value={form.contact_person} onChange={set("contact_person")} className={inputCls} />
              </Field>
              <Field label={t("f_email")} testid="field-email">
                <input data-testid="quote-email-input" type="email" value={form.contact_email} onChange={set("contact_email")} className={inputCls} required dir="ltr" placeholder="ops@airline.com" />
              </Field>
              <Field label={t("f_phone")} testid="field-phone">
                <input data-testid="quote-phone-input" type="tel" value={form.contact_phone} onChange={set("contact_phone")} className={inputCls} dir="ltr" placeholder="+218 ..." />
              </Field>
              <div className="sm:col-span-2">
                <Field label={t("f_details")} testid="field-details">
                  <textarea data-testid="quote-details-textarea" value={form.additional_details} onChange={set("additional_details")} rows={4} className={`${inputCls} resize-none`} />
                </Field>
              </div>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-4">
              <button
                data-testid="quote-form-submit-button"
                type="submit"
                disabled={sending}
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-8 py-4 text-sm font-bold text-white hover:bg-blue-500 disabled:opacity-60 transition-all duration-200 shadow-[0_0_28px_rgba(37,99,235,0.45)] hover:-translate-y-0.5"
              >
                <Send size={16} className="rtl:-scale-x-100" />
                {sending ? t("f_submitting") : t("f_submit")}
              </button>
              {sentRef && (
                <a
                  data-testid="quote-whatsapp-followup"
                  href={`${CONTACT.whatsapp}?text=${encodeURIComponent((lang === "ar" ? "متابعة الطلب رقم " : "Following up on request ") + sentRef)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 px-6 py-3.5 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/10 transition-colors"
                >
                  <MessageCircle size={16} />
                  {t("f_whatsapp_followup")} — {sentRef}
                </a>
              )}
            </div>
          </motion.form>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-14"
          data-testid="hq-map-block"
        >
          <div className="mb-4 flex items-center gap-2.5">
            <MapPin size={18} className="text-blue-400" />
            <h3 className="text-xl font-bold text-white" data-testid="hq-map-title">
              {lang === "ar" ? "موقع المقر الرئيسي — مطار معيتيقة الدولي، طرابلس" : "Headquarters — Mitiga International Airport, Tripoli"}
            </h3>
          </div>
          <div className="overflow-hidden rounded-3xl border border-blue-500/20 shadow-[0_24px_60px_rgba(2,6,17,0.5)]">
            <iframe
              data-testid="hq-map-iframe"
              title="WAS HQ — Mitiga International Airport, Tripoli, Libya"
              src="https://maps.google.com/maps?q=Mitiga%20International%20Airport%2C%20Tripoli%2C%20Libya&z=13&output=embed"
              className="h-[380px] w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <div className="mt-3 flex justify-end">
            <a
              data-testid="hq-map-directions-link"
              href="https://www.google.com/maps/dir/?api=1&destination=Mitiga+International+Airport,+Tripoli,+Libya"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-blue-300 hover:text-blue-200 transition-colors"
            >
              <MapPin size={14} />
              {lang === "ar" ? "احصل على الاتجاهات عبر خرائط جوجل" : "Get directions via Google Maps"}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
